/**
 * _modifiersRest.ts — Modificadores demo únicos para los 11 platos de Almuerzo.
 *
 * Cada plato tiene una configuración distinta para demostrar el sistema en tienda y caja.
 * MOD_ALMUERZO es la plantilla de referencia para almuerzo corriente colombiano.
 */
import type { ModifierGroup } from "@/types";

const g = (
  id: string,
  label: string,
  partial: Omit<ModifierGroup, "id" | "label">,
): ModifierGroup => ({ id, label, ...partial });

const MODALIDAD_LABEL = "¿Dónde lo consume?";

const mkModalidad = (id: string): ModifierGroup =>
  g(id, MODALIDAD_LABEL, {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: `${id}_local`, label: "En el local" },
      {
        id: `${id}_llevar`,
        label: "Para llevar",
        descripcion: "Se cobra el icópor",
        precioExtra: 1000,
      },
    ],
  });

const prependModalidad = (
  prefix: string,
  groups: ModifierGroup[],
): ModifierGroup[] => {
  if (
    groups.some(
      (gr) => gr.label === MODALIDAD_LABEL || gr.id.includes("modalidad"),
    )
  ) {
    return groups;
  }
  return [mkModalidad(`${prefix}_modalidad`), ...groups];
};

const mkComentario = (pfx: string): ModifierGroup =>
  g(`${pfx}_comentario`, "Comentario para preparar", {
    tipoSeleccion: "text",
    required: false,
    placeholderTexto: "Ej: poquito arroz, no tanta salsa, sin cebolla…",
    opciones: [],
  });

const opcionesCambiarSopaPor = (pfx: string) => [
  { id: `${pfx}_sub1`, label: "Más ensalada" },
  { id: `${pfx}_sub2`, label: "Porción de arroz" },
  { id: `${pfx}_sub3`, label: "Huevo frito", precioExtra: 1500 },
  { id: `${pfx}_sub4`, label: "Papa a la francesa" },
  { id: `${pfx}_sub5`, label: "Jugo adicional", precioExtra: 1500 },
  { id: `${pfx}_sub6`, label: "Tajadas" },
  { id: `${pfx}_sub7`, label: "Arepulas" },
];

const opcionesCambiarPrincipioPor = (pfx: string) => [
  { id: `${pfx}_psub1`, label: "Más ensalada" },
  { id: `${pfx}_psub2`, label: "Porción de arroz" },
  { id: `${pfx}_psub3`, label: "Huevo frito", precioExtra: 1500 },
  { id: `${pfx}_psub4`, label: "Papa a la francesa" },
  { id: `${pfx}_psub5`, label: "Jugo adicional", precioExtra: 1500 },
  { id: `${pfx}_psub6`, label: "Tajadas" },
  { id: `${pfx}_psub7`, label: "Arepulas" },
];

/** 0 — Bandeja Paisa */
export const MOD_BANDEJA_PAISA: ModifierGroup[] = [
  g("rest_bp_tamano", "Tamaño de porción", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_bp_t1", label: "Normal" },
      { id: "rest_bp_t2", label: "Grande (+25%)", precioExtra: 5000 },
    ],
  }),
  g("rest_bp_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_bp_b1", label: "Agua" },
      { id: "rest_bp_b2", label: "Gaseosa" },
      { id: "rest_bp_b3", label: "Limonada", precioExtra: 1500 },
      { id: "rest_bp_b4", label: "Jugo natural", precioExtra: 2000 },
    ],
  }),
  g("rest_bp_extras", "Extras paisa", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 3,
    opciones: [
      { id: "rest_bp_e1", label: "Extra aguacate", precioExtra: 2500 },
      { id: "rest_bp_e2", label: "Extra chicharrón", precioExtra: 4000 },
      { id: "rest_bp_e3", label: "Extra chorizo", precioExtra: 3500 },
      { id: "rest_bp_e4", label: "Arepa adicional", precioExtra: 1500 },
    ],
  }),
  g("rest_bp_cubiertos", "¿Cubiertos?", {
    tipoSeleccion: "boolean",
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_bp_c1", label: "Sí, con cubiertos" },
      { id: "rest_bp_c2", label: "No" },
    ],
  }),
  mkComentario("rest_bp"),
];

/** 1 — Arroz con Pollo ($14.000 local; +$1.000 para llevar) */
export const MOD_ARROZ_POLLO: ModifierGroup[] = [
  g("rest_ap_sopa", "Sopa", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ap_s1", label: "Sancocho de pescado" },
      { id: "rest_ap_s2", label: "Ajiaco" },
      {
        id: "rest_ap_s3",
        label: "Sin sopa (solo bandeja)",
        precioExtra: -2000,
      },
      { id: "rest_ap_s4", label: "Cambiar la sopa", exclusiva: true },
    ],
  }),
  g("rest_ap_sustituto", "Cambiar la sopa por", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    visibleCuando: { grupoId: "rest_ap_sopa", optionIds: ["rest_ap_s4"] },
    opciones: opcionesCambiarSopaPor("rest_ap"),
  }),
  g("rest_ap_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ap_b1", label: "Limonada" },
      { id: "rest_ap_b2", label: "Mango" },
    ],
  }),
  g("rest_ap_cubiertos", "Cubiertos", {
    tipoSeleccion: "boolean",
    required: false,
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_ap_c1", label: "Sí, con cubiertos" },
      { id: "rest_ap_c2", label: "No, sin cubiertos" },
    ],
  }),
  g("rest_ap_acomp", "Acompañamientos", {
    tipoSeleccion: "multiple",
    required: false,
    min: 0,
    max: 4,
    accionesRapidas: "todas-ninguna",
    opciones: [
      { id: "rest_ap_a1", label: "Ensalada" },
      { id: "rest_ap_a2", label: "Papa a la francesa" },
      { id: "rest_ap_a3", label: "Tajadas" },
    ],
  }),
  mkComentario("rest_ap"),
];

/** 2 — Sancocho de Gallina */
export const MOD_SANCOCHO: ModifierGroup[] = [
  g("rest_sg_arroz", "¿Con arroz?", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_sg_a1", label: "Con arroz" },
      { id: "rest_sg_a2", label: "Sin arroz" },
      { id: "rest_sg_a3", label: "Arroz aparte", precioExtra: 1500 },
    ],
  }),
  g("rest_sg_picante", "Nivel de picante", {
    tipoSeleccion: "single",
    required: false,
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_sg_p1", label: "Sin picante" },
      { id: "rest_sg_p2", label: "Suave" },
      { id: "rest_sg_p3", label: "Picante 🔥" },
    ],
  }),
  g("rest_sg_extras", "Extras", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 2,
    opciones: [
      { id: "rest_sg_e1", label: "Extra limón" },
      { id: "rest_sg_e2", label: "Extra cilantro" },
      { id: "rest_sg_e3", label: "Aguacate", precioExtra: 2500 },
    ],
  }),
  g("rest_sg_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_sg_b1", label: "Agua" },
      { id: "rest_sg_b2", label: "Gaseosa" },
      { id: "rest_sg_b3", label: "Panela con limón", precioExtra: 2000 },
    ],
  }),
  mkComentario("rest_sg"),
];

/** 3 — Filete de Tilapia */
export const MOD_TILAPIA: ModifierGroup[] = [
  g("rest_ti_acomp", "Acompañamiento", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ti_a1", label: "Arroz + patacón" },
      { id: "rest_ti_a2", label: "Solo arroz" },
      { id: "rest_ti_a3", label: "Solo patacón" },
      { id: "rest_ti_a4", label: "Ensalada verde", precioExtra: 1500 },
    ],
  }),
  g("rest_ti_salsa", "Salsa", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ti_s1", label: "Limón" },
      { id: "rest_ti_s2", label: "Ají casero" },
      { id: "rest_ti_s3", label: "Salsa golf" },
      { id: "rest_ti_s4", label: "Mixta" },
    ],
  }),
  g("rest_ti_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ti_b1", label: "Agua" },
      { id: "rest_ti_b2", label: "Gaseosa" },
      { id: "rest_ti_b3", label: "Cerveza", precioExtra: 5000 },
    ],
  }),
  mkComentario("rest_ti"),
];

/** 4 — Lomo al Plato */
export const MOD_LOMO: ModifierGroup[] = [
  g("rest_lo_termino", "Término de cocción", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_lo_t1", label: "Azul / Sellado" },
      { id: "rest_lo_t2", label: "Término medio" },
      { id: "rest_lo_t3", label: "Tres cuartos" },
      { id: "rest_lo_t4", label: "Bien cocido" },
    ],
  }),
  g("rest_lo_papas", "Papas", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_lo_p1", label: "Salteadas con mantequilla" },
      { id: "rest_lo_p2", label: "Fritas crujientes" },
      { id: "rest_lo_p3", label: "Puré de papa", precioExtra: 1500 },
    ],
  }),
  g("rest_lo_extras", "Extras", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 2,
    opciones: [
      {
        id: "rest_lo_e1",
        label: "Extra cebolla caramelizada",
        precioExtra: 3000,
      },
      {
        id: "rest_lo_e2",
        label: "Extra mantequilla de hierbas",
        precioExtra: 2000,
      },
      { id: "rest_lo_e3", label: "Champiñones salteados", precioExtra: 4000 },
    ],
  }),
  g("rest_lo_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_lo_b1", label: "Agua" },
      { id: "rest_lo_b2", label: "Gaseosa" },
      { id: "rest_lo_b3", label: "Vino tinto copa", precioExtra: 12000 },
    ],
  }),
  mkComentario("rest_lo"),
];

/** 5 — Ajiaco */
export const MOD_AJIACO: ModifierGroup[] = [
  g("rest_aj_crema", "¿Con crema de leche?", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_aj_c1", label: "Con crema" },
      { id: "rest_aj_c2", label: "Sin crema" },
      { id: "rest_aj_c3", label: "Crema aparte" },
    ],
  }),
  g("rest_aj_arroz", "¿Con arroz?", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_aj_a1", label: "Con arroz" },
      { id: "rest_aj_a2", label: "Sin arroz" },
    ],
  }),
  g("rest_aj_extras", "Extras", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 2,
    opciones: [
      { id: "rest_aj_e1", label: "Extra aguacate", precioExtra: 2500 },
      { id: "rest_aj_e2", label: "Extra alcaparras", precioExtra: 1500 },
      { id: "rest_aj_e3", label: "Extra pollo desmechado", precioExtra: 5000 },
    ],
  }),
  g("rest_aj_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_aj_b1", label: "Agua" },
      { id: "rest_aj_b2", label: "Panela con limón", precioExtra: 2000 },
      { id: "rest_aj_b3", label: "Gaseosa" },
    ],
  }),
  mkComentario("rest_aj"),
];

/** 6 — Pasta Boloñesa */
export const MOD_PASTA: ModifierGroup[] = [
  g("rest_pa_tipo", "Tipo de pasta", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_pa_t1", label: "Espagueti" },
      { id: "rest_pa_t2", label: "Penne" },
      { id: "rest_pa_t3", label: "Fettuccine", precioExtra: 1000 },
    ],
  }),
  g("rest_pa_extras", "Extras", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 3,
    opciones: [
      { id: "rest_pa_e1", label: "Extra queso gratinado", precioExtra: 2500 },
      { id: "rest_pa_e2", label: "Pan al ajo", precioExtra: 2000 },
      { id: "rest_pa_e3", label: "Champiñones", precioExtra: 3000 },
      { id: "rest_pa_e4", label: "Aceitunas", precioExtra: 1500 },
    ],
  }),
  g("rest_pa_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_pa_b1", label: "Agua" },
      { id: "rest_pa_b2", label: "Gaseosa" },
      { id: "rest_pa_b3", label: "Vino tinto copa", precioExtra: 12000 },
    ],
  }),
  mkComentario("rest_pa"),
];

/** 7 — Ensalada César */
export const MOD_ENSALADA: ModifierGroup[] = [
  g("rest_ec_proteina", "Proteína extra", {
    tipoSeleccion: "single",
    required: false,
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_ec_p1", label: "Sin extra (pollo incluido)" },
      { id: "rest_ec_p2", label: "Doble pollo", precioExtra: 4000 },
      { id: "rest_ec_p3", label: "Camarones", precioExtra: 8000 },
    ],
  }),
  g("rest_ec_adiciones", "Adiciones", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 3,
    opciones: [
      { id: "rest_ec_a1", label: "Extra crutones" },
      { id: "rest_ec_a2", label: "Extra aderezo césar" },
      { id: "rest_ec_a3", label: "Extra parmesano", precioExtra: 2000 },
      { id: "rest_ec_a4", label: "Aguacate", precioExtra: 2500 },
    ],
  }),
  g("rest_ec_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_ec_b1", label: "Agua" },
      { id: "rest_ec_b2", label: "Limonada", precioExtra: 1500 },
      { id: "rest_ec_b3", label: "Té helado", precioExtra: 2000 },
    ],
  }),
  mkComentario("rest_ec"),
];

/** 8 — Costillas BBQ */
export const MOD_COSTILLAS: ModifierGroup[] = [
  g("rest_cb_salsa", "Salsa BBQ", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_cb_s1", label: "BBQ clásica" },
      { id: "rest_cb_s2", label: "BBQ ahumada" },
      { id: "rest_cb_s3", label: "Honey mustard" },
      { id: "rest_cb_s4", label: "Mixta (mitad y mitad)" },
    ],
  }),
  g("rest_cb_acomp", "Acompañamiento", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_cb_a1", label: "Papas rústicas" },
      { id: "rest_cb_a2", label: "Ensalada de repollo" },
      {
        id: "rest_cb_a3",
        label: "Mixto (papas + ensalada)",
        precioExtra: 1500,
      },
    ],
  }),
  g("rest_cb_extras", "Extras", {
    tipoSeleccion: "multiple",
    min: 0,
    max: 2,
    opciones: [
      { id: "rest_cb_e1", label: "Extra salsa BBQ" },
      { id: "rest_cb_e2", label: "Aros de cebolla", precioExtra: 4000 },
      { id: "rest_cb_e3", label: "Maíz tierno", precioExtra: 2500 },
    ],
  }),
  g("rest_cb_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_cb_b1", label: "Agua" },
      { id: "rest_cb_b2", label: "Gaseosa" },
      { id: "rest_cb_b3", label: "Cerveza artesanal", precioExtra: 6000 },
    ],
  }),
  mkComentario("rest_cb"),
];

/** 9 — Salmón al Grill */
export const MOD_SALMON: ModifierGroup[] = [
  g("rest_sa_termino", "Término del salmón", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_sa_t1", label: "Sellado (centro rosado)" },
      { id: "rest_sa_t2", label: "Completamente cocido" },
    ],
  }),
  g("rest_sa_acomp", "Acompañamiento", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_sa_a1", label: "Puré de papa trufado" },
      { id: "rest_sa_a2", label: "Verduras salteadas" },
      { id: "rest_sa_a3", label: "Mixto (puré + verduras)", precioExtra: 2000 },
      { id: "rest_sa_a4", label: "Arroz pilaf", precioExtra: 1500 },
    ],
  }),
  g("rest_sa_salsa", "Salsa", {
    tipoSeleccion: "single",
    required: false,
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_sa_s1", label: "Mantequilla de limón y hierbas" },
      { id: "rest_sa_s2", label: "Reducción de vino tinto", precioExtra: 3000 },
      { id: "rest_sa_s3", label: "Sin salsa extra" },
    ],
  }),
  g("rest_sa_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_sa_b1", label: "Agua" },
      { id: "rest_sa_b2", label: "Vino blanco copa", precioExtra: 12000 },
      { id: "rest_sa_b3", label: "Cóctel de la casa", precioExtra: 15000 },
    ],
  }),
  mkComentario("rest_sa"),
];

/** Opciones compartidas «Cambiar la sopa / principio por» (almuerzo corriente) */
const OPCIONES_CAMBIAR_SOPA_POR = opcionesCambiarSopaPor("rest_al");
const OPCIONES_CAMBIAR_PRINCIPIO_POR = opcionesCambiarPrincipioPor("rest_al");

/** 10 — Almuerzo Corriente (plantilla demo completa) */
export const MOD_ALMUERZO: ModifierGroup[] = [
  g("rest_al_modalidad", "¿Dónde lo consume?", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_al_mesa", label: "En el local" },
      {
        id: "rest_al_llevar",
        label: "Para llevar",
        descripcion: "Se cobra el icópor",
        precioExtra: 1000,
      },
    ],
  }),
  g("rest_al_sopa", "Sopa", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_al_s1", label: "Sancocho de pescado" },
      { id: "rest_al_s2", label: "Ajiaco" },
      {
        id: "rest_al_s3",
        label: "Sin sopa (solo bandeja)",
        precioExtra: -2000,
      },
      { id: "rest_al_s4", label: "Cambiar la sopa", exclusiva: true },
    ],
  }),
  g("rest_al_sustituto", "Cambiar la sopa por", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    visibleCuando: { grupoId: "rest_al_sopa", optionIds: ["rest_al_s4"] },
    opciones: OPCIONES_CAMBIAR_SOPA_POR,
  }),
  g("rest_al_principio", "Principio", {
    tipoSeleccion: "multiple",
    required: false,
    min: 0,
    max: 2,
    opciones: [
      { id: "rest_al_p1", label: "Frijol" },
      { id: "rest_al_p2", label: "Garbanzo" },
      { id: "rest_al_p3", label: "Acelga guisada" },
      { id: "rest_al_p4", label: "Cambiar el principio", exclusiva: true },
    ],
  }),
  g("rest_al_sustituto_principio", "Cambiar el principio por", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    visibleCuando: { grupoId: "rest_al_principio", optionIds: ["rest_al_p4"] },
    opciones: OPCIONES_CAMBIAR_PRINCIPIO_POR,
  }),
  g("rest_al_proteina", "Proteína", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_al_r1", label: "Res" },
      { id: "rest_al_r2", label: "Cerdo" },
      { id: "rest_al_r3", label: "Pechuga" },
      { id: "rest_al_r4", label: "Hígado encebollado" },
      { id: "rest_al_r5", label: "Carne en bistec" },
      { id: "rest_al_r6", label: "Pierna" },
      { id: "rest_al_r7", label: "Pernil dorado" },
      {
        id: "rest_al_r8",
        label: "Pechuga gratinada",
        precioExtra: 1000,
        descripcion: "Vale igual con o sin sopa",
        anulaAjustesDeGrupos: ["rest_al_sopa"],
      },
      {
        id: "rest_al_r9",
        label: "Mojarra",
        precioExtra: 4000,
        descripcion: "Vale igual con o sin sopa",
        anulaAjustesDeGrupos: ["rest_al_sopa"],
      },
    ],
  }),
  g("rest_al_bebida", "Bebida", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "rest_al_b1", label: "Limonada" },
      { id: "rest_al_b2", label: "Mango" },
    ],
  }),
  g("rest_al_cubiertos", "Cubiertos", {
    tipoSeleccion: "boolean",
    required: false,
    min: 0,
    max: 1,
    opciones: [
      { id: "rest_al_c1", label: "Sí, con cubiertos" },
      { id: "rest_al_c2", label: "No, sin cubiertos" },
    ],
  }),
  g("rest_al_acomp", "Acompañamientos", {
    tipoSeleccion: "multiple",
    required: false,
    min: 0,
    max: 4,
    accionesRapidas: "todas-ninguna",
    opciones: [
      { id: "rest_al_a1", label: "Arepulas" },
      { id: "rest_al_a2", label: "Tajadas" },
      { id: "rest_al_a3", label: "Ensalada", stock: 4 },
      { id: "rest_al_a4", label: "Patacón" },
    ],
  }),
  g("rest_al_comentario", "Comentario para preparar", {
    tipoSeleccion: "text",
    required: false,
    placeholderTexto: "Ej: poquito arroz, no tanta salsa, sin cebolla…",
    opciones: [],
  }),
];

/** Índice por producto cat_rest_0 … cat_rest_10 */
export const REST_MODIFIERS_BY_INDEX: ModifierGroup[][] = [
  prependModalidad("rest_bp", MOD_BANDEJA_PAISA),
  prependModalidad("rest_ap", MOD_ARROZ_POLLO),
  prependModalidad("rest_sg", MOD_SANCOCHO),
  prependModalidad("rest_ti", MOD_TILAPIA),
  prependModalidad("rest_lo", MOD_LOMO),
  prependModalidad("rest_aj", MOD_AJIACO),
  prependModalidad("rest_pa", MOD_PASTA),
  prependModalidad("rest_ec", MOD_ENSALADA),
  prependModalidad("rest_cb", MOD_COSTILLAS),
  prependModalidad("rest_sa", MOD_SALMON),
  MOD_ALMUERZO,
];
